import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { XIcon } from "lucide-react";
import { focusManager } from "@tanstack/react-query";
import { UserStatus } from "@/generated-api";
import { useActiveSubscription, useGetUserProfile } from "@/tanstack-hooks";
import { useTranslation } from "@/hooks/useTranslation";

const DISMISSED_ON_KEY = "trialBannerDismissedOn";

// YYYY-MM-DD in the user's local time zone.
function localToday(): string {
  return new Date().toLocaleDateString("en-CA");
}

function readDismissedOn(): string | null {
  try {
    return localStorage.getItem(DISMISSED_ON_KEY);
  } catch {
    return null;
  }
}

/**
 * Thin white bar pinned to the top of the app for trial-eligible users,
 * nudging them to activate their gifted free trial. Dismissable for the rest
 * of the day (local time) — the dismissal date is persisted, so it survives
 * reloads and app restarts, and the banner comes back the next day.
 */
export default function TrialBanner() {
  const userProfileQuery = useGetUserProfile();
  const status = userProfileQuery.data?.status;
  // /subscription/active 400s for users still onboarding. Only fetch once
  // they're active; flipping enabled on approval refetches the errored query.
  const isActiveUser =
    status === UserStatus.ACTIVE || status === UserStatus.ACTIVE_HIDDEN;
  const activeSubscriptionQuery = useActiveSubscription(false, isActiveUser);
  const t = useTranslation();
  const [dismissedOn, setDismissedOn] = useState(readDismissedOn);
  const [today, setToday] = useState(localToday);

  // The app can stay open (or suspended in background) across midnight, so
  // re-read the date whenever it regains focus. On native, main.tsx drives
  // focusManager from Capacitor's appStateChange.
  useEffect(() => {
    return focusManager.subscribe((focused) => {
      if (focused) setToday(localToday());
    });
  }, []);

  const trialEligible = activeSubscriptionQuery.data?.trial_eligible ?? false;
  if (!trialEligible || dismissedOn === today) return null;

  function handleDismiss() {
    const now = localToday();
    setToday(now);
    setDismissedOn(now);
    try {
      localStorage.setItem(DISMISSED_ON_KEY, now);
    } catch {
      // Storage unavailable — the dismissal still holds for this session.
    }
  }

  return (
    <div className="relative flex items-center justify-center bg-white px-9 py-2 text-center text-sm text-black">
      <span>
        {t.TRIAL_BANNER.LABELS.GIFTED}{" "}
        <Link to="/upgrade" className="font-semibold underline">
          {t.TRIAL_BANNER.LABELS.ACTIVATE}
        </Link>
      </span>
      <button
        type="button"
        onClick={handleDismiss}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-black/50 hover:text-black"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
